import React, { useState, useEffect } from 'react';

import Navigation from './Navigation';
import Hero from './Hero';
import PersonalBrand from './PersonalBrand';
import AIEra from './AIEra';
import WorkImpact from './WorkImpact';
import Capabilities from './Capabilities';
import Projects from './Projects';
import Timeline from './Timeline';
import ArchitectureShowcase from './ArchitectureShowcase';
import CurrentWork from './CurrentWork';
import CurrentFocus from './CurrentFocus';
import Mission from './Mission';
import Contact from './Contact';
import Footer from './Footer';

const ParticleBackground = React.lazy(() => import('./ParticleBackground'));

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [showEntry, setShowEntry] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleEnter = () => {
    setShowEntry(false);
    setLoading(true);
    setTimeout(() => setLoading(false), 800);
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 600;
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  };

  useEffect(() => {
    if (showEntry || loading) return;
    const handleScroll = () => {
      const sections = ['hero', 'brand', 'aiera', 'work', 'capabilities', 'projects', 'timeline', 'contact'];
      const scrollPos = window.scrollY + 150;
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && scrollPos >= el.offsetTop && scrollPos < el.offsetTop + el.offsetHeight) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showEntry, loading]);

  if (showEntry) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0f] flex items-center justify-center z-50">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-indigo-500/25">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
            </div>
            <div className="space-y-1 mb-8">
              <h1 className="text-4xl font-bold text-white">Ranjithkumar Sekar</h1>
              <p className="text-gray-500 text-sm">Application Engineer | Automation & AI</p>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent mb-8" />
          </div>
          <button onClick={handleEnter}
            className="group relative inline-flex items-center justify-center px-8 py-3.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 hover:scale-105">
            <span className="relative flex items-center space-x-2">
              <span>Enter Portfolio</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>
          <div className="mt-8 flex justify-center space-x-2">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" style={{ animationDelay: '0.2s' }} />
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0f] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-2 border-indigo-500/20 rounded-full animate-spin border-t-indigo-500" />
            <div className="absolute inset-2 border-2 border-transparent border-t-purple-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.6s' }} />
          </div>
          <p className="text-gray-500 text-sm font-mono animate-pulse">Loading systems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <React.Suspense fallback={null}>
        <ParticleBackground />
      </React.Suspense>
      <Navigation activeSection={activeSection} />
      <main className="relative z-10">
        <Hero />
        <PersonalBrand />
        <AIEra />
        <WorkImpact />
        <Capabilities />
        <Projects />
        <Timeline />
        <ArchitectureShowcase />
        <CurrentWork />
        <CurrentFocus />
        <Mission />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default Portfolio;
